// Error references, PII-free local error log, global handlers, and the
// nurse-friendly netErr() copy helper (ported from legacy netErr, upgraded
// with error refs per Blueprint: every user-facing failure carries a ref the
// founder can grep out of the local ring buffer).

const RING_KEY = 'tumaini2_errlog';
export const RING_CAP = 50;

// Whitelisted keys for a stored entry — NOTHING else is ever persisted.
// No payloads, no URLs with params, no row data: patient PII must never
// reach localStorage via the error path.
export const ENTRY_KEYS = Object.freeze(['ref', 'at', 'source', 'name', 'code', 'message']);
const MSG_CAP = 300;

/**
 * New error reference: 'TU-' + base36 timestamp + 2 base36 random chars.
 * Short enough to read over the phone, unique enough for a 50-entry log.
 * @returns {string}
 */
export function newRef() {
  const t = Date.now().toString(36).toUpperCase();
  const r = Math.floor(Math.random() * 1296).toString(36).toUpperCase().padStart(2, '0');
  return 'TU-' + t + r;
}

/**
 * Reduce an arbitrary raw entry to the whitelisted, size-capped shape.
 * Pure — tested directly.
 * @param {Record<string, any>} raw
 * @returns {Record<string, string>}
 */
export function sanitizeEntry(raw) {
  /** @type {Record<string, string>} */
  const out = {};
  for (const k of ENTRY_KEYS) {
    const v = raw ? raw[k] : undefined;
    if (v === undefined || v === null) continue;
    out[k] = String(v).slice(0, MSG_CAP);
  }
  return out;
}

/**
 * Pure ring-buffer step: append the entry, dropping oldest past the cap.
 * @param {any[]} ring current entries (oldest first)
 * @param {any} entry
 * @param {number} [cap]
 * @returns {any[]} new ring
 */
export function pushRing(ring, entry, cap = RING_CAP) {
  const next = Array.isArray(ring) ? ring.slice() : [];
  next.push(entry);
  while (next.length > cap) next.shift();
  return next;
}

/**
 * Persist an error entry to the localStorage ring buffer. Storage failures
 * (quota, privacy mode, corrupt JSON) are swallowed — the error path must
 * never itself throw.
 * @param {Record<string, any>} raw
 * @param {{getItem(k:string):string|null, setItem(k:string,v:string):void}} [storage]
 * @returns {Record<string, string>} the sanitized entry that was stored (or would have been)
 */
export function logError(raw, storage) {
  const entry = sanitizeEntry(Object.assign({ at: new Date().toISOString() }, raw));
  try {
    const store = storage || /** @type {any} */ (globalThis).localStorage;
    if (store) {
      let ring = [];
      try {
        ring = JSON.parse(store.getItem(RING_KEY) || '[]');
      } catch {
        ring = [];
      }
      store.setItem(RING_KEY, JSON.stringify(pushRing(ring, entry)));
    }
  } catch {
    // storage unavailable — the entry is still returned for console use
  }
  return entry;
}

/**
 * Read the ring buffer (for the admin diagnostics view). Never throws.
 * @param {{getItem(k:string):string|null}} [storage]
 * @returns {any[]}
 */
export function readErrorLog(storage) {
  try {
    const store = storage || /** @type {any} */ (globalThis).localStorage;
    return JSON.parse((store && store.getItem(RING_KEY)) || '[]');
  } catch {
    return [];
  }
}

/** Default network-failure copy: the caller was WRITING, so say so plainly. */
export const NET_WRITE_MSG = 'No connection — this was NOT saved. Check your network and try again.';
/**
 * Network-failure copy for a READ (a refresh, a re-fetch). "This was NOT
 * saved" is alarming and wrong when nothing was being saved — it sends a nurse
 * hunting for lost work that was never at risk. The reassurance that matters
 * on a failed read is the opposite one: what is on screen is old, not gone.
 */
export const NET_READ_MSG = 'No connection — this could not refresh. What you see may be out of date.';

/**
 * Nurse-friendly error copy — ported from legacy netErr() with refs added.
 * A network/connectivity failure gets an unambiguous warning; anything else
 * falls back to the caller's labelled copy. The driver error's name, code and
 * message go to console + ring buffer under the returned ref — never into the
 * toast (no schema/policy internals in user-facing copy). The console gets the
 * SAME three fields as the ring, never the raw object (review R-09): a
 * PostgREST error's `details`/`hint` echo the offending value — a unique
 * violation prints the patient's phone — and DevTools on a shared clinic
 * terminal is no place for that.
 * @param {any} error caught error / supabase error object
 * @param {string} [fallback] user-facing copy for non-network failures
 * @param {{netMsg?: string}} [opts] override the network-failure copy — pass
 *   NET_READ_MSG when the failed operation was a read, not a write
 * @returns {string} friendly copy ending in ' · ref TU-…'
 */
export function netErr(error, fallback, opts) {
  const ref = newRef();
  const friendly = /fetch|network|load failed|timeout/i.test((error && error.message) || '')
    ? (opts && opts.netMsg) || NET_WRITE_MSG
    : fallback || 'Something went wrong';
  const fields = {
    name: error && error.name,
    code: error && error.code,
    message: error && error.message,
  };
  logError(Object.assign({ ref, source: 'netErr' }, fields));
  console.error(friendly, 'ref', ref, fields);
  return friendly + ' · ref ' + ref;
}

/**
 * Install window.onerror + unhandledrejection loggers. Idempotent per window.
 * @param {any} [win] defaults to globalThis
 * @returns {void}
 */
export function installGlobalHandlers(win) {
  const w = win || /** @type {any} */ (globalThis);
  if (w._tumainiErrorsInstalled) return;
  w._tumainiErrorsInstalled = true;
  w.addEventListener('error', (/** @type {any} */ ev) => {
    logError({
      ref: newRef(),
      source: 'onerror',
      name: ev.error && ev.error.name,
      message: ev.message,
    });
  });
  w.addEventListener('unhandledrejection', (/** @type {any} */ ev) => {
    const r = ev.reason;
    logError({
      ref: newRef(),
      source: 'unhandledrejection',
      name: r && r.name,
      code: r && r.code,
      message: (r && r.message) || String(r),
    });
  });
}
